import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, ChevronLeft, ChevronRight, Coins, ExternalLink, History, PieChart, RefreshCw, Search, Trophy, Users, X,
} from 'lucide-react';
import {
  apiService, type MoneyLeaderboardResponse, type MoneyLeaderboardRowDTO, type VaultTransactionDTO,
} from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { formatPlayerName } from '@/utils/playerName';
import { transactionMotif, transactionOrigin } from '@/utils/moneyCategories';
import {
  DEFAULT_PAGE_SIZE, PAGE_SIZES, SEARCH_DELAY_MS, formatMoney, formatShare, formatSignedMoney, historyError,
  leaderboardError, medal, pageLabel, shortUuid, topShare,
} from './moneyLeaderboard';
import styles from './MoneyLeaderboardSection.module.scss';

/** Lignes de l'historique d'un joueur par page, comme sur sa fiche. */
const HISTORY_PAGE = 10;

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function playerLabel(row: { uuid: string; name?: string; displayName?: string }): string {
  return formatPlayerName(row.name, row.displayName, shortUuid(row.uuid));
}

/**
 * Section « Classement » de l'espace Statistiques (rubrique Économie) : les joueurs les plus riches,
 * avec leur rang, leur pseudo, leur solde et leur part de la monnaie en circulation, lus en direct dans
 * la base de l'argent par le satellite du projet. Une recherche retrouve un joueur et son rang ; un clic
 * sur une ligne ouvre l'historique de son argent, avec l'origine et le motif de chaque mouvement.
 */
export function MoneyLeaderboardSection() {
  const navigate = useNavigate();
  const hasPage = useAuthStore((s) => s.hasPage);
  const gameId = useGameStore((s) => s.activeGameId);
  const groupKey = useActiveGameGroup()?.key ?? null;

  const [data, setData] = useState<MoneyLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  /** Part des 10 plus riches, gardée de la dernière lecture du haut du classement. */
  const [top10, setTop10] = useState<number | null>(null);
  const [selected, setSelected] = useState<MoneyLeaderboardRowDTO | null>(null);
  // Une réponse arrivée après une autre demande (frappe rapide, changement de page) est ignorée.
  const requestId = useRef(0);

  // La recherche part un instant après la dernière touche, et repart de la première page.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(input.trim());
      setPage(0);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [input]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const res = await apiService.getMoneyLeaderboard(page, size, query);
    if (id !== requestId.current) return;
    if (res.data) {
      setData(res.data);
      setError(null);
      if (!res.data.query && res.data.page === 0) setTop10(topShare(res.data.players));
    } else {
      setError(leaderboardError(res.status, res.error));
    }
    setLoading(false);
  }, [page, size, query, gameId, groupKey]);

  useEffect(() => { void load(); }, [load]);

  // Changement de projet ou de groupe : rien de l'ancien classement ne doit rester affiché.
  useEffect(() => {
    setData(null);
    setTop10(null);
    setSelected(null);
    setPage(0);
  }, [gameId, groupKey]);

  const canOpenSheet = hasPage('players');
  const rows = data?.players ?? [];
  const searching = !!data?.query;
  const totalPages = data?.totalPages ?? 1;

  const cards = useMemo(() => ([
    {
      icon: Users,
      value: data ? data.totalPlayers.toLocaleString('fr-FR') : '-',
      label: "Comptes d'argent",
      sub: 'Un par joueur venu au moins une fois',
    },
    {
      icon: Coins,
      value: data ? formatMoney(data.totalMoney) : '-',
      label: 'Monnaie en circulation',
      sub: 'Somme des soldes positifs',
    },
    {
      icon: PieChart,
      value: top10 !== null ? formatShare(top10) : '-',
      label: 'Détenu par les 10 plus riches',
      sub: 'Part de la monnaie en circulation',
    },
  ]), [data, top10]);

  if (loading && !data && !error) {
    return (
      <div className={styles.state}>
        <div className={styles.spinner} />
        <p>Lecture du classement...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={styles.state}>
        <AlertCircle size={32} />
        <h3>Classement illisible</h3>
        <p>{error}</p>
        <button className={styles.button} onClick={() => void load()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.cards}>
        {cards.map((c) => (
          <div key={c.label} className={styles.card}>
            <div className={styles.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={styles.cardText}>
              <span className={styles.cardValue}>{c.value}</span>
              <span className={styles.cardLabel}>{c.label}</span>
              <span className={styles.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <label className={styles.search}>
          <Search size={16} />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Rechercher un pseudo, un gamertag ou un uuid"
            aria-label="Rechercher un joueur dans le classement"
          />
          {input && (
            <button type="button" className={styles.clear} onClick={() => setInput('')} title="Effacer la recherche">
              <X size={14} />
            </button>
          )}
        </label>
        <select
          className={styles.select}
          value={size}
          onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          disabled={searching}
          title="Joueurs par page"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} par page</option>)}
        </select>
        <button className={styles.buttonGhost} onClick={() => void load()} disabled={loading} title="Relire les soldes">
          <RefreshCw size={16} className={loading ? styles.spin : undefined} /> Actualiser
        </button>
      </div>

      {error && <div className={styles.errorBox}><AlertCircle size={16} /><span>{error}</span></div>}
      {data?.namesPartial && (
        <div className={styles.notice}>
          <AlertCircle size={16} />
          <span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span>
        </div>
      )}

      <div className={styles.tableCard}>
        <div className={styles.tableHead}>
          <h3><Trophy size={18} /> {searching ? `Recherche « ${data?.query} »` : 'Les plus riches'}</h3>
          <span className={styles.tableCount}>
            {searching
              ? `${rows.length} joueur${rows.length > 1 ? 's' : ''} trouvé${rows.length > 1 ? 's' : ''}`
              : pageLabel(page, totalPages)}
          </span>
        </div>

        {rows.length === 0 ? (
          <p className={styles.empty}>
            {searching
              ? "Aucun joueur trouvé. La recherche porte sur le début du pseudo ou du gamertag des joueurs déjà venus, ou sur un uuid entier."
              : "Aucun compte d'argent pour le moment : ils se créent à la première connexion de chaque joueur."}
          </p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Joueur</th>
                  <th className={styles.num}>Solde</th>
                  <th className={styles.num}>Part de la monnaie</th>
                  <th aria-label="Historique" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = medal(r.rank);
                  return (
                    <tr key={r.uuid} className={styles.row} onClick={() => setSelected(r)} title="Voir l'historique de son argent">
                      <td>
                        <span className={`${styles.rank} ${m ? styles[m] : ''}`}>{r.rank.toLocaleString('fr-FR')}</span>
                      </td>
                      <td>
                        <div className={styles.player}>
                          <span className={r.online ? styles.dotOnline : styles.dotOffline} title={r.online ? 'En ligne' : 'Hors ligne'} />
                          <div className={styles.playerText}>
                            <span className={styles.playerName}>{playerLabel(r)}</span>
                            {r.online && r.serverName && <span className={styles.playerSub}>sur {r.serverName}</span>}
                          </div>
                        </div>
                      </td>
                      <td className={`${styles.num} ${r.money < 0 ? styles.negative : ''}`}>{formatMoney(r.money)}</td>
                      <td className={styles.num}>
                        <div className={styles.share}>
                          <span>{formatShare(r.share)}</span>
                          <span className={styles.shareBar}><span style={{ width: `${Math.min(100, Math.max(0, r.share))}%` }} /></span>
                        </div>
                      </td>
                      <td className={styles.historyCell}><History size={16} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!searching && totalPages > 1 && (
          <div className={styles.pagination}>
            <button className={styles.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={loading || page === 0}>
              <ChevronLeft size={16} /> Précédent
            </button>
            <span>{pageLabel(page, totalPages)}</span>
            <button className={styles.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={loading || page + 1 >= totalPages}>
              Suivant <ChevronRight size={16} />
            </button>
          </div>
        )}

        <p className={styles.hint}>
          Soldes lus en direct dans la base de l'argent. Deux soldes égaux ont le même rang, comme dans le classement
          « Argent » en jeu (/app, Classement).
        </p>
      </div>

      {selected && (
        <MoneyHistoryModal
          player={selected}
          canOpenSheet={canOpenSheet}
          onOpenSheet={() => navigate(`/players/${selected.uuid}`)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** Historique de l'argent d'un joueur, avec l'origine et le motif de chaque mouvement. */
function MoneyHistoryModal({ player, canOpenSheet, onOpenSheet, onClose }: {
  player: MoneyLeaderboardRowDTO;
  canOpenSheet: boolean;
  onOpenSheet: () => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<VaultTransactionDTO[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    void apiService.getMoneyTransactionsByUuid(player.uuid, page, HISTORY_PAGE).then((res) => {
      if (id !== requestId.current) return;
      if (res.data) {
        setItems(res.data.items);
        setTotalPages(res.data.totalPages);
        setTotal(res.data.total);
        setError(null);
      } else {
        // Une base qui ne répond pas n'est pas un historique vide : le dire.
        setItems([]);
        setError(historyError(res.status, res.error));
      }
      setLoading(false);
    });
  }, [player.uuid, page]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.modalHead}>
          <div>
            <h3>Historique de l'argent de {playerLabel(player)}</h3>
            <span className={styles.modalSub}>
              Rang {player.rank.toLocaleString('fr-FR')}, solde {formatMoney(player.money)}
              {total > 0 && `, ${total.toLocaleString('fr-FR')} mouvement${total > 1 ? 's' : ''}`}
            </span>
          </div>
          <button className={styles.close} onClick={onClose} title="Fermer"><X size={18} /></button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.state}><div className={styles.spinner} /><p>Lecture de l'historique...</p></div>
          ) : error ? (
            <div className={styles.errorBox}><AlertCircle size={16} /><span>{error}</span></div>
          ) : items.length === 0 ? (
            <p className={styles.empty}>Aucun mouvement d'argent pour ce joueur.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={`${styles.table} ${styles.historyTable}`}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Origine</th>
                    <th>Motif</th>
                    <th className={styles.num}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.id}>
                      <td className={styles.nowrap}>{formatDateTime(t.createdDate)}</td>
                      <td>{transactionOrigin(t)}</td>
                      <td>{transactionMotif(t)}</td>
                      <td className={`${styles.num} ${t.amount >= 0 ? styles.positive : styles.negative}`}>
                        {formatSignedMoney(t.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.modalFoot}>
          {totalPages > 1 ? (
            <div className={styles.pagination}>
              <button className={styles.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={loading || page === 0}>
                <ChevronLeft size={16} /> Plus récents
              </button>
              <span>{pageLabel(page, totalPages)}</span>
              <button className={styles.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={loading || page + 1 >= totalPages}>
                Plus anciens <ChevronRight size={16} />
              </button>
            </div>
          ) : <span />}
          {canOpenSheet && (
            <button className={styles.buttonGhost} onClick={onOpenSheet}>
              <ExternalLink size={16} /> Ouvrir sa fiche
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
